<template>
  <figure :class="$b()">
    <div :class="$b('controls')">
      <BaseLink
        :class="$b('arrow', { hidden: !leftArrowVisible })"
        data-left-arrow
        is-button
        :disabled="!leftArrowVisible"
      >
        <VisionIcon
          icon="arrow"
          colour="black"
          size="small"
          :rotate="180"
        />
      </BaseLink>
      <BaseLink
        :class="$b('arrow', { hidden: !rightArrowVisible })"
        data-right-arrow
        is-button
        :disabled="!rightArrowVisible"
      >
        <VisionIcon
          icon="arrow"
          colour="black"
          size="small"
        />
      </BaseLink>
    </div>
    <Swiper
      :class="$b('images')"
      :modules="modules"
      slides-per-view="auto"
      :space-between="22.5"
      :navigation="{
        nextEl: '[data-right-arrow]',
        prevEl: '[data-left-arrow]',
      }"
      @slide-change="handleSlideChange"
    >
      <SwiperSlide
        v-for="image in images"
        :key="image.id"
      >
        <DatoCmsImage
          :class="$b('img')"
          :data="image.responsiveImage"
          :picture-class="$b('picture')[0]"
          :picture-style="getObjectPosition(image)"
        />
      </SwiperSlide>
    </Swiper>

    <ArticleImageFooter
      :caption="caption"
      :credit="credit"
    />
  </figure>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import { Image as DatoCmsImage } from 'vue-datocms'

export default {
  name: 'ArticleCarousel',

  components: {
    Swiper,
    SwiperSlide,
    DatoCmsImage,
  },

  props: {
    images: {
      type: Array,
      required: true,
    },

    caption: {
      type: String,
      default: null,
    },

    credit: {
      type: String,
      default: null,
    },
  },

  setup() {
    return {
      modules: [Navigation],
    }
  },

  data: () => ({
    leftArrowVisible: false,
    rightArrowVisible: false,
  }),

  mounted() {
    this.rightArrowVisible = this.images.length > 1
  },

  methods: {
    getObjectPosition(image) {
      if (!image.focalPoint) return

      const xPoint = `${image.focalPoint.x * 100}%`
      const yPoint = `${image.focalPoint.y * 100}%`

      return { objectPosition: `${xPoint} ${yPoint}` }
    },
    handleSlideChange(swiper) {
      this.leftArrowVisible = swiper.realIndex > 0
      this.rightArrowVisible = swiper.realIndex + 1 < this.images.length
    },
  },
}
</script>

<style lang="postcss">
.ArticleCarousel {
  .ArticleCarousel__images {
    @apply w-screen;

    margin-bottom: 20px;
    --carousel-gutter: var(--outer-gutter);
    --left: calc(
      var(--carousel-gutter) + var(--column-width) * 3 + var(--inner-gutter) * 3
    );
    margin-left: calc(0px - var(--left));
    margin-right: calc(0px - var(--carousel-gutter));
    padding-left: var(--left);
    padding-right: var(--carousel-gutter);

    @screen lgMax {
      --left: var(--carousel-gutter);
    }

    @screen xxl {
      --carousel-gutter: calc(
        ((100vw - theme('screens.xxl')) / 2) + var(--outer-gutter)
      );
    }

    @screen sm {
      margin-bottom: 24px;
    }

    .swiper-slide {
      max-width: calc(var(--column-width) * 8 + var(--inner-gutter) * 7);

      @screen lgMax {
        max-width: 85vw;
      }
    }

    &:not(.swiper-initialized) .swiper-wrapper {
      @apply space-x-sm;
    }

    .swiper-wrapper {
      @apply flex flex-row;
    }

    .swiper-scrollbar {
      @apply mt-xxs mx-auto
        relative left-0;

      border-radius: 18px;
      -ms-touch-action: none;
      background-color: transparent;
      height: 5px;
      max-width: 1500px;
      width: 100%;
    }

    .swiper-scrollbar-drag {
      @apply bg-grey transition-colors;

      height: 100%;
      width: 100%;
      position: relative;
      border-radius: 10px;
      left: 0;
      top: 0;

      &:hover {
        @apply bg-off-black bg-opacity-30;
      }
    }

    .swiper-scrollbar-cursor-drag {
      cursor: move;
    }

    .swiper-scrollbar-lock {
      display: none;
    }
  }
}

.ArticleCarousel__controls {
  @apply flex justify-end
    space-x-sm
    mb-8
    mdMax:hidden;
}

.ArticleCarousel__arrow {
  @apply opacity-100 transition-opacity;

  &--hidden {
    @apply opacity-0 pointer-events-none;
  }
}

.ArticleCarousel__picture {
  @apply object-cover;
}

.ArticleCarousel__img {
  @apply w-full h-full;

  max-height: 80vh;
  border-radius: 10px;
  object-fit: cover;
}
</style>
